import { ChartTooltipContent } from "@/components/Chart";
import { BaseInsightLineChart } from "@/components/insights/BaseInsightLineChart";
import { PRIMARY_AXIS_COLOR } from "@/components/insights/insights-constants";
import { useActiveEnvironmentsTimeSeries } from "@/queries/insights-queries";
import { formatChartDate, type TimeRange } from "@/routes/insights/insights";
import { type Timestamp } from "@bufbuild/protobuf";
import { useMemo, type FC } from "react";
import * as RechartsPrimitive from "recharts";

type PointPayload = {
    timestamp: Timestamp;
    activeEnvironments: number;
    date: Date;
};

type Props = {
    startTimestamp: Timestamp;
    endTimestamp: Timestamp;
    selectedRange: TimeRange;
    projectId?: string;
};
export const ActiveEnvironmentsLineChart: FC<Props> = ({ startTimestamp, endTimestamp, selectedRange, projectId }) => {
    const { data, resolution, isLoading } = useActiveEnvironmentsTimeSeries(
        startTimestamp,
        endTimestamp,
        selectedRange,
        projectId,
    );

    const chartData = useMemo(() => {
        if (!data?.activeEnvironments) return [];

        const chartData = data.activeEnvironments.map((point) => {
            const dateObj = point.time ? point.time.toDate() : new Date();
            const activeEnvironments = point.value ? Number(point.value) : 0;

            return {
                date: dateObj,
                timestamp: dateObj.getTime(),
                activeEnvironments,
            };
        });

        return chartData;
    }, [data]);

    return (
        <div className="w-full">
            <BaseInsightLineChart
                isLoading={isLoading}
                chartData={chartData}
                chartConfig={{
                    activeEnvironments: {
                        label: "Active Environments",
                        color: PRIMARY_AXIS_COLOR,
                    },
                }}
                yAxisConfig={{
                    width: 40,
                    tickFormatter: (value: number) => value.toLocaleString(),
                    domain: [0, "auto"],
                    minTickGap: 20,
                    allowDecimals: false,
                }}
                tooltipContent={
                    <ChartTooltipContent
                        labelFormatter={(_: string | number | Date, payload: unknown[]) => {
                            if (!payload || payload.length === 0) return "";
                            const item = payload[0] as { payload: PointPayload };
                            if (!(item.payload.date instanceof Date)) return "";

                            return formatChartDate(
                                item.payload.date,
                                resolution,
                                startTimestamp,
                                endTimestamp,
                            );
                        }}
                    />
                }
            >
                <RechartsPrimitive.Line
                    type="monotone"
                    dataKey="activeEnvironments"
                    name="activeEnvironments"
                    stroke="var(--color-activeEnvironments)"
                    strokeWidth={2}
                    dot={{ r: 0 }}
                    activeDot={{
                        r: 6,
                        stroke: "var(--color-activeEnvironments)",
                        strokeWidth: 2,
                        fill: "rgb(var(--ona-orange-100))",
                    }}
                    xAxisId="0"
                />
            </BaseInsightLineChart>
        </div>
    );
};
